import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { adm, apiErr } from "../../lib/adminApi";
import { PageHead, TInput, TArea, Sel, Toggle, Btn, Card, MediaPicker, Badge, ApprovalSel } from "../../components/admin/ui";
import TeaserStudio from "../../components/admin/TeaserStudio";
import StreamingLinksEditor from "../../components/admin/StreamingLinksEditor";
import CreditsEditor from "../../components/admin/CreditsEditor";
import ProductMultiPicker from "../../components/admin/ProductMultiPicker";
import { isValidStreamUrl } from "../../lib/streaming";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MODES = [
  { v: "FULL_PUBLIC", l: "FULL PUBLIC STREAM" },
  { v: "TEASER", l: "TEASER ONLY" },
  { v: "LOCKED", l: "LOCKED / NOT AVAILABLE" },
  { v: "PURCHASE_REQUIRED", l: "PURCHASE REQUIRED (OWN TO LISTEN)" },
  { v: "INNER_CIRCLE", l: "INNER CIRCLE MEMBERS" },
  { v: "VAULT_ENTITLEMENT", l: "VAULT ENTITLEMENT" },
  { v: "SCHEDULED", l: "SCHEDULED" },
];
const PUB = ["draft", "scheduled", "published", "archived"];

function TeaserUploadBtn({ onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const up = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("accessClass", "PUBLIC");
      fd.append("title", `${file.name} (custom teaser)`);
      const m = await adm.uploadMedia(fd);
      onUploaded(m.id);
      toast.success("Custom teaser uploaded (any length).");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <input type="file" accept="audio/*" hidden ref={ref}
        onChange={(e) => e.target.files?.[0] && up(e.target.files[0])} data-testid="teaser-upload-input" />
      <Btn variant="ghost" onClick={() => ref.current?.click()} disabled={busy} testid="teaser-upload-btn">
        {busy ? "UPLOADING…" : "UPLOAD NEW TEASER"}
      </Btn>
    </>
  );
}

function TeaserConfig({ t, idx, set, openStudio }) {
  const source = t.teaserSource || (t.customTeaserMediaId ? "UPLOADED_CUSTOM" : "GENERATED_FROM_MASTER");
  const gen = t.generatedTeaser;
  const outdated = gen && t.fullMediaId && gen.sourceMasterMediaId !== t.fullMediaId;

  return (
    <div className="md:col-span-3 border border-gold/20 bg-[#0e0d0a] p-4" data-testid={`track-${idx}-teaser-config`}>
      <p className="font-mono text-[9px] tracking-[0.3em] text-gold mb-3">TEASER SOURCE — SWITCHING NEVER DELETES EITHER ASSET</p>
      <div className="flex flex-wrap gap-2.5 mb-4">
        {[["GENERATED_FROM_MASTER", "GENERATE FROM FULL TRACK"], ["UPLOADED_CUSTOM", "UPLOADED CUSTOM TEASER"]].map(([v, l]) => (
          <button key={v} onClick={() => set("teaserSource", v)} data-testid={`track-${idx}-source-${v}`}
            className={`font-mono text-[9px] tracking-[0.2em] border px-4 py-2.5 transition-colors ${
              source === v ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/45 hover:border-white/40"}`}>
            {l} {source === v && "· ACTIVE"}
          </button>
        ))}
      </div>

      {outdated && (
        <div className="border border-amber-500/40 bg-amber-500/5 p-3 mb-4" data-testid={`track-${idx}-teaser-outdated`}>
          <p className="font-mono text-[9px] tracking-[0.2em] text-amber-400">
            ⚠ THE GENERATED TEASER WAS CUT FROM A PREVIOUS MASTER AND MAY BE OUTDATED.
          </p>
          <Btn variant="ghost" onClick={openStudio} className="mt-2" testid={`track-${idx}-regenerate`}>REGENERATE TEASER</Btn>
        </div>
      )}

      {source === "GENERATED_FROM_MASTER" ? (
        <div data-testid={`track-${idx}-generated-panel`}>
          {gen ? (
            <div className="flex flex-wrap items-center gap-4">
              <p className="font-mono text-[9px] text-white/50">
                CURRENT: {gen.duration}″ FROM {Math.floor(gen.start / 60)}:{String(Math.floor(gen.start % 60)).padStart(2, "0")}
                {gen.fadeIn ? ` · FADE IN ${gen.fadeIn}s` : ""}{gen.fadeOut ? ` · FADE OUT ${gen.fadeOut}s` : ""}
              </p>
              <audio controls preload="metadata" className="h-8 max-w-[260px]" src={`${API}/media/public/${gen.mediaId}`} />
            </div>
          ) : (
            <p className="font-mono text-[9px] text-white/35">NO GENERATED TEASER YET.</p>
          )}
          <div className="mt-3">
            <Btn onClick={openStudio} disabled={!t.fullMediaId || !t.id} testid={`track-${idx}-open-studio`}>
              {gen ? "OPEN TEASER STUDIO (REGENERATE)" : "OPEN TEASER STUDIO"}
            </Btn>
            {!t.fullMediaId && <p className="font-mono text-[8px] text-white/30 mt-2">UPLOAD THE FULL / MASTER AUDIO FIRST.</p>}
            {t.fullMediaId && !t.id && <p className="font-mono text-[8px] text-white/30 mt-2">SAVE THE RELEASE ONCE BEFORE OPENING THE STUDIO.</p>}
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4 items-end" data-testid={`track-${idx}-custom-panel`}>
          <MediaPicker label="CUSTOM TEASER (PUBLIC AUDIO ASSET — ANY LENGTH)" value={t.customTeaserMediaId}
            onChange={(v) => set("customTeaserMediaId", v || null)} mode="id" filterType="audio" testid={`track-${idx}-custom-teaser`} />
          <div className="flex items-center gap-3 pb-1">
            <TeaserUploadBtn onUploaded={(id) => { set("customTeaserMediaId", id); set("teaserSource", "UPLOADED_CUSTOM"); }} />
            {t.customTeaserMediaId && (
              <audio controls preload="metadata" className="h-8 max-w-[220px]" src={`${API}/media/public/${t.customTeaserMediaId}`} />
            )}
          </div>
        </div>
      )}
      <p className="font-mono text-[8px] text-white/25 mt-4">
        VISITORS ONLY EVER RECEIVE THE TEASER FILE — THE MASTER IS NEVER SENT TO THE BROWSER IN TEASER MODE.
      </p>
    </div>
  );
}

function TrackEditor({ releaseId, t, idx, update, remove, move, onStudioResult }) {
  const [open, setOpen] = useState(false);
  const [studio, setStudio] = useState(false);
  const set = (k, v) => update(idx, { ...t, [k]: v });
  const setEd = (k, v) => update(idx, { ...t, editorial: { ...(t.editorial || {}), [k]: v } });
  const setEa = (k, v) => update(idx, { ...t, editorialApproval: { ...(t.editorialApproval || {}), [k]: v } });
  const setSocial = (k, v) => update(idx, { ...t, social: { ...(t.social || {}), [k]: v } });
  const mode = t.availabilityType === "PREVIEW" ? "TEASER" : t.availabilityType;

  return (
    <div className="border border-white/10 bg-[#0c0c0c]" data-testid={`track-editor-${t.trackNumber}`}>
      <div className="flex items-center gap-3 px-4 py-3">
        <span className="font-mono text-[10px] text-gold w-6">{String(t.trackNumber).padStart(2, "0")}</span>
        <button onClick={() => setOpen(!open)} className="flex-1 text-left text-sm text-white/85 truncate">{t.title || "Untitled"}</button>
        {t.fullMediaId && <Badge tone="green">MASTER</Badge>}
        <Badge tone={["FULL_PUBLIC", "TEASER"].includes(mode) ? "gold" : "dim"}>{mode}</Badge>
        <button onClick={() => move(idx, -1)} className="text-white/40 hover:text-white text-xs px-1">↑</button>
        <button onClick={() => move(idx, 1)} className="text-white/40 hover:text-white text-xs px-1">↓</button>
        <button onClick={() => remove(idx)} className="text-red-400/70 hover:text-red-400 text-xs px-1" data-testid={`track-remove-${t.trackNumber}`}>✕</button>
      </div>
      {open && (
        <div className="border-t border-white/10 p-4 grid md:grid-cols-3 gap-4">
          <TInput label="TITLE" value={t.title} onChange={(v) => set("title", v)} testid={`track-${idx}-title`} />
          <TInput label="TRACK SLUG (SHAREABLE PAGE /track/…)" value={t.slug} onChange={(v) => set("slug", v || null)} testid={`track-${idx}-slug`} />
          <TInput label="DURATION" value={t.duration} onChange={(v) => set("duration", v)} placeholder="3:45" />
          <MediaPicker label="TRACK ARTWORK (OPTIONAL — DEFAULTS TO RELEASE)" value={t.artwork} onChange={(v) => set("artwork", v || null)} mode="url" filterType="image" testid={`track-${idx}-artwork`} />
          <div className="flex items-end pb-1"><Toggle label="EXPLICIT" checked={!!t.explicit} onChange={(v) => set("explicit", v)} /></div>
          <div />

          <div className="md:col-span-3 border-t border-white/10 pt-4">
            <p className="font-mono text-[9px] tracking-[0.3em] text-gold mb-3">AUDIO &amp; PUBLIC PLAYBACK</p>
          </div>
          <MediaPicker
            label={mode === "FULL_PUBLIC" ? "FULL / MASTER AUDIO (WILL STREAM PUBLICLY)" : "FULL / MASTER AUDIO (PROTECTED — NEVER PUBLIC)"}
            value={t.fullMediaId} onChange={(v) => set("fullMediaId", v || null)} mode="id" filterType="audio" testid={`track-${idx}-full-media`} />
          <Sel label="PUBLIC PLAYBACK MODE" value={mode} onChange={(v) => set("availabilityType", v)}
            options={MODES} testid={`track-${idx}-availability`} />
          <TInput label="LOCK LABEL (OPTIONAL — e.g. ALBUM ONLY)" value={t.lockLabel} onChange={(v) => set("lockLabel", v || null)} />
          {mode === "SCHEDULED" && (
            <TInput label="SCHEDULED AT (ISO)" value={t.scheduledAt} onChange={(v) => set("scheduledAt", v || null)} type="datetime-local" />
          )}
          {mode === "PURCHASE_REQUIRED" && (
            <TInput label="REQUIRED PURCHASE LOOKUP KEYS (COMMA)" value={(t.requiredLookupKeys || []).join(",")}
              onChange={(v) => set("requiredLookupKeys", v.split(",").map((s) => s.trim()).filter(Boolean))} />
          )}
          {mode === "VAULT_ENTITLEMENT" && (
            <TInput label="REQUIRED ENTITLEMENT (DIGITAL ID)" value={t.requiredEntitlement} onChange={(v) => set("requiredEntitlement", v || null)} />
          )}

          {mode === "TEASER" && (
            <TeaserConfig t={t} idx={idx} set={set} openStudio={() => setStudio(true)} />
          )}

          <TInput label="TRACK VIDEO URL" value={t.videoUrl} onChange={(v) => set("videoUrl", v || null)} />
          <TInput label="CREDITS" value={t.credits} onChange={(v) => set("credits", v)} className="md:col-span-2" />
          <div className="md:col-span-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[9px] tracking-[0.25em] text-white/45">LYRICS — NEVER AUTO-PUBLISHED</span>
              <ApprovalSel value={t.editorialApproval?.lyrics} onChange={(v) => setEa("lyrics", v)} testid={`track-${idx}-lyrics-approval`} />
            </div>
            <TArea label="" value={t.editorial?.lyrics} onChange={(v) => setEd("lyrics", v || null)} rows={5} testid={`track-${idx}-lyrics`} />
          </div>
          <div className="md:col-span-3">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[9px] tracking-[0.25em] text-white/45">BEHIND THE MEANING — NEVER AUTO-PUBLISHED</span>
              <ApprovalSel value={t.editorialApproval?.meaning} onChange={(v) => setEa("meaning", v)} testid={`track-${idx}-meaning-approval`} />
            </div>
            <TArea label="" value={t.editorial?.meaning} onChange={(v) => setEd("meaning", v || null)} rows={4} testid={`track-${idx}-meaning`} />
          </div>

          <div className="md:col-span-3 border-t border-white/10 pt-4">
            <p className="font-mono text-[9px] tracking-[0.3em] text-gold mb-3">SOCIAL PREVIEW CARD (SHARE LINK: /api/share/track/{t.slug || "…"})</p>
          </div>
          <TInput label="SOCIAL TITLE (DEFAULT: TRACK — MOTION & MEANING)" value={t.social?.title} onChange={(v) => setSocial("title", v || null)} testid={`track-${idx}-social-title`} />
          <TInput label="SOCIAL DESCRIPTION" value={t.social?.description} onChange={(v) => setSocial("description", v || null)} testid={`track-${idx}-social-desc`} />
          <MediaPicker label="SOCIAL ARTWORK (DEFAULT: TRACK/RELEASE ART)" value={t.social?.image} onChange={(v) => setSocial("image", v || null)} mode="url" filterType="image" testid={`track-${idx}-social-image`} />
        </div>
      )}
      {studio && (
        <TeaserStudio releaseId={releaseId} track={t} onClose={() => setStudio(false)}
          onGenerated={(res) => onStudioResult(idx, res)} />
      )}
    </div>
  );
}

export default function AdminReleaseEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { adm.get("releases", id).then(setR).catch(() => toast.error("Not found")); }, [id]);
  if (!r) return <p className="text-white/40 font-mono text-xs">LOADING…</p>;

  const set = (k, v) => setR((x) => ({ ...x, [k]: v }));
  const setCa = (f, v) => setR((x) => ({ ...x, copyApproval: { ...(x.copyApproval || {}), [f]: v } }));
  const setSocial = (k, v) => setR((x) => ({ ...x, social: { ...(x.social || {}), [k]: v } }));
  const updateTrack = (idx, t) => setR((x) => ({ ...x, trackList: x.trackList.map((o, i) => (i === idx ? t : o)) }));
  const onStudioResult = (idx, res) => setR((x) => ({
    ...x,
    trackList: x.trackList.map((o, i) => (i === idx
      ? { ...o, generatedTeaser: res.generatedTeaser, teaserSource: res.teaserSource } : o)),
  }));
  const removeTrack = (idx) => {
    if (!window.confirm("Remove this track?")) return;
    setR((x) => ({ ...x, trackList: x.trackList.filter((_, i) => i !== idx).map((t, i) => ({ ...t, trackNumber: i + 1 })) }));
  };
  const moveTrack = (idx, dir) => setR((x) => {
    const list = [...x.trackList];
    const j = idx + dir;
    if (j < 0 || j >= list.length) return x;
    [list[idx], list[j]] = [list[j], list[idx]];
    return { ...x, trackList: list.map((t, i) => ({ ...t, trackNumber: i + 1 })) };
  });
  const addTrack = () => setR((x) => ({ ...x, trackList: [...(x.trackList || []), {
    trackNumber: (x.trackList?.length || 0) + 1, title: "New Track", duration: "", explicit: false,
    availabilityType: "LOCKED", editorial: { lyrics: null, meaning: null } }] }));

  const save = async () => {
    const bad = (Array.isArray(r.streamingLinks) ? r.streamingLinks : []).find(
      (s) => s.enabled !== false && !isValidStreamUrl(s.url));
    if (bad) {
      toast.error(`Streaming link "${bad.platform || "untitled"}" needs a valid https:// URL — or disable it.`);
      return;
    }
    setSaving(true);
    try {
      const { _id, id: _skip, source, createdAt, updatedAt, ...body } = r;
      const saved = await adm.update("releases", id, body);
      setR(saved);
      toast.success("Release saved.");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const destroy = async () => {
    if (!window.confirm(`Delete "${r.title}" permanently? Consider Archived status instead.`)) return;
    try { await adm.remove("releases", id); toast.success("Deleted."); navigate("/admin/releases"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div data-testid="admin-release-edit">
      <Link to="/admin/releases" className="font-mono text-[9px] tracking-[0.25em] text-white/40 hover:text-gold">← ALL RELEASES</Link>
      <PageHead title={r.title} sub={`/${r.slug} · ${r.trackList?.length || 0} tracks`}>
        <Btn variant="danger" onClick={destroy} testid="release-delete">DELETE</Btn>
        <Btn onClick={save} disabled={saving} testid="release-save">{saving ? "SAVING…" : "SAVE"}</Btn>
      </PageHead>

      <Card className="grid md:grid-cols-3 gap-4">
        <TInput label="TITLE" value={r.title} onChange={(v) => set("title", v)} testid="release-title" />
        <TInput label="SLUG" value={r.slug} onChange={(v) => set("slug", v)} testid="release-slug" />
        <Sel label="RELEASE TYPE" value={r.releaseType} onChange={(v) => set("releaseType", v)}
          options={["Album", "EP", "Single"].map((v) => ({ v, l: v }))} testid="release-type" />
        <Sel label="PUBLICATION" value={r.publicationStatus} onChange={(v) => set("publicationStatus", v)}
          options={PUB.map((v) => ({ v, l: v.toUpperCase() }))} testid="release-pubstatus" />
        {r.publicationStatus === "scheduled" && (
          <TInput label="SCHEDULED AT" value={r.scheduledAt} onChange={(v) => set("scheduledAt", v || null)} type="datetime-local" />
        )}
        <Sel label="DIMENSION" value={r.dimension || ""} onChange={(v) => set("dimension", v || null)}
          options={[{ v: "", l: "—" }, ...["01", "02", "03", "04"].map((v) => ({ v, l: `DIMENSION ${v}` }))]} />
        <TInput label="DISPLAY STATUS (e.g. OUT NOW)" value={r.status} onChange={(v) => set("status", v)} />
        <TInput label="RELEASE DATE / YEAR" value={r.releaseDate} onChange={(v) => set("releaseDate", v)} />
        <div className="flex items-end pb-1"><Toggle label="FEATURED ON HOMEPAGE" checked={!!r.featured} onChange={(v) => set("featured", v)} testid="release-featured" /></div>
        <MediaPicker label="ARTWORK URL" value={r.artwork} onChange={(v) => { set("artwork", v); set("cover", v); }} mode="url" filterType="image" testid="release-artwork" />
        <ProductMultiPicker label="ASSOCIATED STORE PRODUCTS (OWNERSHIP / PURCHASE MAPPING)"
          value={r.purchaseLookupKeys || []} onChange={(v) => set("purchaseLookupKeys", v)}
          field="lookupKey" testid="release-products" />
        <div className="md:col-span-3 grid md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[9px] tracking-[0.25em] text-white/45">DESCRIPTION</span>
              <ApprovalSel value={r.copyApproval?.description} onChange={(v) => setCa("description", v)} testid="release-approval-description" />
            </div>
            <TArea label="" value={r.description} onChange={(v) => set("description", v)} rows={2} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[9px] tracking-[0.25em] text-white/45">ARTISTIC STATEMENT</span>
              <ApprovalSel value={r.copyApproval?.statement} onChange={(v) => setCa("statement", v)} testid="release-approval-statement" />
            </div>
            <TArea label="" value={r.statement} onChange={(v) => set("statement", v)} rows={2} />
          </div>
        </div>
        <CreditsEditor value={r.credits} onChange={(v) => set("credits", v)} />
        <StreamingLinksEditor value={r.streamingLinks} onChange={(v) => set("streamingLinks", v)} />
        <div className="md:col-span-3 border-t border-white/10 pt-4">
          <p className="font-mono text-[9px] tracking-[0.3em] text-gold mb-3">SOCIAL PREVIEW CARD (SHARE LINK: /api/share/release/{r.slug})</p>
        </div>
        <TInput label="SOCIAL TITLE" value={r.social?.title} onChange={(v) => setSocial("title", v || null)} testid="release-social-title" />
        <TInput label="SOCIAL DESCRIPTION" value={r.social?.description} onChange={(v) => setSocial("description", v || null)} testid="release-social-desc" />
        <MediaPicker label="SOCIAL ARTWORK" value={r.social?.image} onChange={(v) => setSocial("image", v || null)} mode="url" filterType="image" testid="release-social-image" />
      </Card>

      <div className="flex items-center justify-between mt-8 mb-4">
        <p className="font-mono text-[10px] tracking-[0.3em] text-gold">TRACKS — ORDER IS ALWAYS PRESERVED PUBLICLY</p>
        <Btn variant="ghost" onClick={addTrack} testid="track-add">ADD TRACK</Btn>
      </div>
      <div className="space-y-2">
        {(r.trackList || []).map((t, i) => (
          <TrackEditor key={t.id || i} releaseId={id} t={t} idx={i} update={updateTrack}
            remove={removeTrack} move={moveTrack} onStudioResult={onStudioResult} />
        ))}
      </div>
      <div className="mt-6 flex justify-end"><Btn onClick={save} disabled={saving} testid="release-save-bottom">{saving ? "SAVING…" : "SAVE RELEASE"}</Btn></div>
    </div>
  );
}
